import { supplementalFacilityAnswers } from './state-supplemental-answers.ts';
import { facilityExtraValidation } from './report-extra-validation.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
import { isValidPhone } from './phone.ts';
import { STATE_FIELDS_YEAR } from './state-fields.ts';

export function facilityIdentityIssues(meta: FacilityMeta): string[] {
  const issues: string[] = [...facilityExtraValidation(meta).errors];
  const answers = supplementalFacilityAnswers(meta.stateReporting, meta.state);
  for (const [key, label, role] of [
    ['ownerOperatorPhone24', 'Owner/operator 24-hour phone', 'Owner / Operator'],
  ] as const) {
    const phone = answers[key];
    if (phone !== null && phone !== '') {
      if (!isValidPhone(phone)) issues.push(label + ' must be a valid phone number.');
      if (!meta.contacts.some((contact) => contact.contactTypes.includes(role)))
        issues.push('Add the ' + role + ' name in Facility Report Information before supplying its phone.');
    }
  }

  if (!meta.dunBradstreetCodes.some((id) => id.trim()))
    issues.push('A Dun & Bradstreet ID is required; public utilities and non-business entities may enter N/A.');
  if (meta.dunBradstreetCodes.some((id) => !id.trim())) issues.push('Remove blank Dun & Bradstreet IDs.');
  const signed = meta.dateSigned;
  if (
    signed &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(signed) ||
      !Number.isFinite(Date.parse(signed)) ||
      new Date(signed).toISOString().slice(0, 10) !== signed)
  )
    issues.push('The certification date must be a valid date.');
  else if (signed && signed < meta.reportingYear + '-01-01')
    issues.push('The certification date cannot be earlier than January 1 of the reporting year.');
  for (const contact of meta.contacts) {
    const roles = contact.contactTypes;
    if (
      roles.some(
        (role) =>
          role === 'Owner / Operator' ||
          role === 'Tier II Information Contact' ||
          (role === 'Submitter' && meta.state === 'ME' && meta.reportingYear === STATE_FIELDS_YEAR),
      ) &&
      !contact.phones.some((phone) => isValidPhone(phone.number))
    )
      issues.push(contact.name + ': a valid phone is required.');
    if (
      (roles.some((role) => role === 'Emergency Contact' || role === 'Fac. Emergency Coordinator') ||
        (meta.state === 'RI' && meta.reportingYear === STATE_FIELDS_YEAR && roles.includes('Owner / Operator'))) &&
      !contact.phones.some((phone) => phone.type === '24-hour' && isValidPhone(phone.number))
    )
      issues.push(contact.name + ': a valid 24-hour phone is required.');
  }
  return issues;
}

export function storageAmountIssue(amount: number | null): string | null {
  return amount !== null && (!Number.isFinite(amount) || Math.round(amount) <= 0)
    ? 'Storage maximum amount must round to at least one pound for export.'
    : null;
}

/** Draft inventory may contain zero; positive whole-pound values are required for an exported entry. */
export function reportQuantityIssues(maximum: number | null, average: number | null): string[] {
  const issues: string[] = [];
  for (const [name, amount] of [
    ['Maximum', maximum],
    ['Average', average],
  ] as const) {
    if (amount !== null && (!Number.isFinite(amount) || Math.round(amount) <= 0))
      issues.push(name + ' amount must round to at least one pound for export.');
  }
  return issues;
}
